import { TenantSettingsKey } from '@core/api/tenant/tenant.enums';
import { TenantDto, TenantSettingsDto, TenantSettingsResponse, TenantsResponse } from '@core/api/tenant/tenant.models';

import { SiteStoreModel } from '@domain/dashboard/dashboard.models';
import { mapSiteTypeApiModelEnumToStoreModelEnum } from '@domain/single-site/single-site-enums.mappings';
import { mapGeoPositionApiModelToStoreModel } from '@domain/single-site/single-site-models.mappings';

import { isDefined } from '@shared/utils/is-defined';
import { sortSitesByName, sortStringsAsc } from '@shared/utils/sort-utils';

import { mapOrgTypeApiModelEnumToStoreModelEnum } from './tenant-enums.mappings';
import { FeatureFlags } from './tenant.enums';
import { TenantDtoStoreModel, TenantsResponseStoreModel } from './tenant.models';

export const mapTenantDtoApiModelToStoreModel = (tenant: TenantDto): TenantDtoStoreModel => {
  return {
    id: tenant.id,
    name: tenant.name,
    orgType: mapOrgTypeApiModelEnumToStoreModelEnum(tenant.orgType),
    authType: tenant.authType,
    marketRegion: tenant.marketRegion,
    baseCurrency: tenant.baseCurrency,
    ownSites: tenant.ownSites.map(mapSiteApiModelToStoreModel),
    assignedSites: tenant.assignedSites.map(mapSiteApiModelToStoreModel),
    tenantSettings: tenant.tenantSettings,
  };
};

export const mapSiteApiModelToStoreModel = (site: TenantDto['ownSites'][number]): SiteStoreModel => ({
  id: site.id,
  name: site.name,
  siteType: mapSiteTypeApiModelEnumToStoreModelEnum(site.siteType),
  timeZone: site.timeZone,
  tenant: site.tenant,
  centerCoordinates: isDefined(site.centerCoordinates) ? mapGeoPositionApiModelToStoreModel(site.centerCoordinates) : undefined,
  features: site.features,
});

export const mapTenantsResponseApiModelToStoreModel = (response: TenantsResponse): TenantsResponseStoreModel => {
  return {
    tenants: response.tenants
      .map((tenant) => mapTenantDtoApiModelToStoreModel(tenant))
      .sort((first, second) => sortStringsAsc(first.name, second.name)),
  };
};

export const getAccessibleSites = (tenants: TenantDtoStoreModel[] | undefined): SiteStoreModel[] | undefined => {
  if (!tenants) {
    return;
  }

  return sortSitesByName([
    ...new Map(tenants.flatMap((tenant) => [...tenant.ownSites, ...tenant.assignedSites]).map((site) => [site.id, site])).values(),
  ]);
};

export const mapTenantSettingsToFeatureFlags = (response: TenantSettingsResponse): FeatureFlags[] => {
  const flags: FeatureFlags[] = [];

  if (doesSettingsHaveKey(response.settings, TenantSettingsKey.WebappRoamingEnabled)) {
    flags.push(FeatureFlags.Roaming);
  }

  if (doesSettingsHaveKey(response.settings, TenantSettingsKey.WebappSharingEnabled)) {
    flags.push(FeatureFlags.Sharing);
  }

  if (doesSettingsHaveKey(response.settings, TenantSettingsKey.WebappFlexibilityEnabled)) {
    flags.push(FeatureFlags.Flexibility);
  }

  if (doesSettingsHaveKey(response.settings, TenantSettingsKey.WebappAiAssistantEnabled)) {
    flags.push(FeatureFlags.AiAssistant);
  }

  if (doesSettingsHaveKey(response.settings, TenantSettingsKey.WebappPredictiveAnalyticsEnabled)) {
    flags.push(FeatureFlags.PredictiveAnalytics);
  }

  if (doesSettingsHaveKey(response.settings, TenantSettingsKey.WebappDisplayTimezonesEnabled)) {
    flags.push(FeatureFlags.DisplayTimezones);
  }

  return flags;
};

export const doesSettingsHaveKey = (settings: TenantSettingsDto[], key: string): boolean => {
  return settings.some(
    (setting) =>
      setting.key === key &&
      (setting.value === true || (typeof setting.value === 'string' && setting.value && setting.value.toLowerCase() === 'true'))
  );
};
